import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { Checkbox, Form, Upload, Image } from 'antd';
import LoadableButton from "../../../../../components/buttons/LoadableButton.jsx";
import { DepositAmountApi, getBankDataApi } from '../../../../../api/request/wallet/cashdeposit.js';
import UploadDoc from '../../../../../assets/UploadDoc.jsx';
// tncFileUrl is passed as a prop
import { handleApiError } from '../../../../../utils/handleApiError.js';
import { Link } from 'react-router-dom';

const CashDepositMobile = ({ amount, resetAll, tncFileUrl, loginTerminalId, validateAmount, paymentMethod }) => {
    const [form] = Form.useForm();
    const { userDetails } = useSelector(state => state.user);
    const [isLoading, setIsLoading] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [message, setMessage] = useState('');
    const [previewImage, setPreviewImage] = useState('');
    const [bankData, setBankData] = useState({})
    const [signatureFileList, setSignatureFileList] = useState([]);

    const getBase64 = (file) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
    });

    const extractOriginFileObjs = (fileArray) => {
        return fileArray ? fileArray.map((file) => file.originFileObj) : [];
    };

    const handleSubmit = async (values) => {
        setIsLoading(true);
        try {
            if (validateAmount) {
                toast.error(validateAmount);
                return;
            }
            if (!amount) {
                toast.error('Please enter deposit amount');
                return;
            }
            const UploadDocument = extractOriginFileObjs(values?.UploadDocument);
            const isWallet = loginTerminalId === 'wallet';
            let formData = new FormData();
            formData.append("paymentgatewayId", paymentMethod?.id);
            formData.append("UserId", userDetails?._id);
            formData.append("ReferenceId", values?.ReferenceId);
            formData.append("Comment", values?.Comment || "");
            formData.append("Amount", Number(amount).toFixed(2));
            formData.append("DepositType", 3);
            formData.append("loginTerminalId", isWallet ? 0 : loginTerminalId);
            UploadDocument.forEach((file) => formData.append("PaymentProof", file));

            let apiHeaders = { headers: { Accept: "application/json", "Content-Type": "multipart/form-data" } };

            const { data } = await DepositAmountApi(formData, apiHeaders);
            if (data?.success) {
                toast.success(data?.message);
                setSignatureFileList([]);
                form.resetFields();
                resetAll();
            }
        } catch (error) {
            handleApiError(error);
        } finally {
            setIsLoading(false);
        }
    }

    const handlePreview = async (file) => {
        if (!file.url && !file.preview) {
            file.preview = await getBase64(file.originFileObj);
        }
        setPreviewImage(file.url || file.preview);
        setPreviewOpen(true);
    };

    const handleSignatureChange = ({ fileList: newSignatureList }) => {
        setSignatureFileList(newSignatureList);
        form.setFieldsValue({ UploadDocument: newSignatureList });
    };

    const UploadButton = (
        <button type="button">
            <div className='flex items-center justify-center gap-2 border rounded-md py-1.5 px-4 text-lg text-gray-500 font-medium'>
                <img src="/assets/icons/Upload_Document_icon.svg" alt="icon" className='h-5' />
                <span>Click to Upload</span>
            </div>
        </button>
    );

    const getBankDetails = async () => {
        try {
            const { data } = await getBankDataApi()
            setBankData(data?.data[0]);
        } catch (err) { }
    }

    useEffect(() => {
        getBankDetails()
    }, [])


    return (
        <div>
            <Form form={form} layout="vertical" className='space-y-5 mb-6' onFinish={handleSubmit}>
                {/* Bank Details Section */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
                    <h3 className="text-lg font-semibold text-blue-600 pb-2 mb-4 border-b border-gray-200">Bank Details</h3>

                    <div className="grid grid-cols-2 gap-6">
                        {/* Left Column */}
                        <div className="space-y-4">
                            {/* Bank Name */}
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">
                                    Bank Name
                                </label>
                                <p className="text-sm text-[#90A1B9]">{bankData?.bankName}</p>
                            </div>

                            {/* Account Number */}
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">
                                    Ac Number
                                </label>
                                <p className="text-sm text-[#90A1B9]">{bankData?.accountNumber}</p>
                            </div>

                            {/* Branch */}
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">
                                    Branch Address
                                </label>
                                <p className="text-sm text-[#90A1B9]">{bankData?.branchAddress}</p>
                            </div>
                        </div>

                        {/* Right Column */}
                        <div className="space-y-4">
                            {/* Holder Name */}
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">
                                    Holder Name
                                </label>
                                <p className="text-sm text-[#90A1B9]">{bankData?.accountHolderName}</p>
                            </div>

                            {/* IFSC Code */}
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">
                                    IBAN No
                                </label>
                                <p className="text-sm text-[#90A1B9]">{bankData?.ibanNo}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Document Upload Section */}
                <div className="mb-6">
                    <Form.Item
                        name="UploadDocument"
                        rules={[{ required: true, message: 'Please upload at least one document!' }]}
                        className="mb-0 w-full custom-form-item"
                    >
                        <Upload.Dragger
                            name="files"
                            multiple
                            maxCount={2}
                            listType="picture"
                            accept=".pdf,.jpg,.jpeg,.png"
                            fileList={signatureFileList}
                            onChange={handleSignatureChange}
                            beforeUpload={(file) => {
                                if (signatureFileList.length >= 2) {
                                    setMessage("You can only upload up to 2 files.");
                                    return Upload.LIST_IGNORE; // ❌ Block upload
                                }
                                return false; // Prevent auto upload
                            }}
                            onPreview={handlePreview}
                            className="upload-dragger-custom w-full"
                            style={{
                                borderRadius: '16px',
                                border: '2px dashed #d1d5db',
                                backgroundColor: '#f9fafb'
                            }}
                        >
                            <div className="p-6 text-center">
                                {/* Upload Icon */}
                                <div className="flex justify-center mb-4">
                                    <div className="w-12 h-12 bg-blue-500 rounded-2xl flex items-center justify-center">
                                        <UploadDoc color="white" />
                                    </div>
                                </div>

                                {/* Upload Text */}
                                <h3 className="text-lg font-medium text-gray-700 mb-2">
                                    Upload your Document
                                </h3>

                                <p className="text-sm text-gray-500">
                                    Click or drag files to upload
                                </p>

                                {/* <p className="text-xs text-gray-400 mt-2">
                                Supports: PDF, JPG, PNG files
                            </p> */}
                            </div>
                        </Upload.Dragger>
                    </Form.Item>
                    {message && (
                        <p className="text-red-500 text-sm mt-2">
                            {message}
                        </p>
                    )}
                </div>

                {/* Image Preview Modal */}
                {
                    previewImage && (
                        <Image
                            wrapperStyle={{ display: 'none' }}
                            preview={{
                                visible: previewOpen,
                                onVisibleChange: (visible) => setPreviewOpen(visible),
                                afterOpenChange: (visible) => !visible && setPreviewImage(''),
                            }}
                            src={previewImage}
                        />
                    )
                }

                <div>
                    <Form.Item
                        name='conditions'
                        valuePropName="checked"
                        rules={[{ validator: (_, value) => value ? Promise.resolve() : Promise.reject(new Error('Please accept the terms and conditions.')) }]}
                    >
                        <div className="flex items-center gap-2">
                            <Checkbox className='m-0'>Yes, I agreed to the</Checkbox>
                            <Link to={'/terms_&_conditions'} className='primary hover:underline duration-300'>Terms and conditions</Link>
                        </div>
                    </Form.Item>
                </div>

                <LoadableButton
                    className='bg-primary text-sm text-white font-semibold rounded-lg px-12 py-3 mt-4 w-full uppercase themeHover duration-500'
                    type="Submit"
                    lable='Submit'
                    isLoading={isLoading}
                    loadingLable='Adding request...'
                />
            </Form>
        </div >
    )
}

export default CashDepositMobile
